import { Grid, ScrollArea, Stack, Text, Title } from '@mantine/core';
import { capitalize } from 'lodash';
import moment from 'moment';
import { useMemo } from 'react';

import { TSDData } from '@e-origin/contracts';
import { EORIGIN_DATE_FORMAT_WITH_TIME } from '@e-origin/shared';

import { NOT_AVAILABLE } from '@constants';
import { getControlStatusReason, getLastMessageCode, getStatusLabel } from '../../utils';
import { CustomsStateNotifications } from './CustomsStateNotifications';
import { getErrorDescription } from './errorDescriptions';
import { InvalidationSection } from './InvalidationSection';

interface TSDViewCustomsProps {
  tsd: TSDData;
}

export const TSDViewCustoms: React.FC<TSDViewCustomsProps> = ({ tsd }) => {
  const status = useMemo(() => getStatusLabel(tsd.generalInfo?.status), [tsd.generalInfo?.status]);

  if (!tsd?.customsState) {
    return (
      <Stack m="md" ml="xl">
        Not sent yet
      </Stack>
    );
  }
  const customsMessageCode = getLastMessageCode(tsd.customsState.customsLogs);

  return (
    <ScrollArea h="100%" offsetScrollbars="y">
      <Stack mx="xl">
        <Grid mb="md">
          <Grid.Col span={{ base: 6, xl: 2 }}>
            <Stack gap="xs">
              <Text c="gray" size="sm">
                Status
              </Text>
              <Text fw={500}>{status}</Text>
            </Stack>
          </Grid.Col>
          <Grid.Col span={{ base: 6, xl: 3 }}>
            <Stack gap="xs">
              <Text c="gray" size="sm">
                Correlation ID
              </Text>
              <Text fw={500} data-pw="correlation-id-value">
                {tsd.customsState?.correlationId ?? 'N/A'}
              </Text>
            </Stack>
          </Grid.Col>
          {tsd.messageInfo?.MRN && (
            <Grid.Col span={{ base: 6, xl: 3 }}>
              <Stack gap="xs">
                <Text c="gray" size="sm">
                  Movement reference number
                </Text>
                <Text fw={500}>{tsd.messageInfo.MRN}</Text>
              </Stack>
            </Grid.Col>
          )}
          {tsd.customsState?.businessValidationType && (
            <Grid.Col span={{ base: 6, xl: 3 }}>
              <Stack gap="xs">
                <Text c="gray" size="sm">
                  Business validation type
                </Text>
                <Text fw={500}>{tsd.customsState.businessValidationType}</Text>
              </Stack>
            </Grid.Col>
          )}{' '}
          {customsMessageCode && (
            <Grid.Col span={{ base: 6, xl: 3 }}>
              <Stack gap="xs">
                <Text c="gray" size="sm">
                  Customs Message Code
                </Text>
                <Text fw={500}>{customsMessageCode}</Text>
              </Stack>
            </Grid.Col>
          )}
        </Grid>

        {tsd.customsState?.dateOfAcceptance && (
          <Grid mb="md">
            <Grid.Col span={2.5}>
              <Stack gap="xs">
                <Text c="gray" size="sm">
                  Date of acceptance
                </Text>
                <Text fw={500}>{moment(tsd.customsState?.dateOfAcceptance).format(EORIGIN_DATE_FORMAT_WITH_TIME)}</Text>
              </Stack>
            </Grid.Col>
          </Grid>
        )}

        <Stack>
          {tsd.customsState?.control && (
            <Stack mb="md">
              <Grid>
                <Grid.Col span={3}>
                  <Stack gap="xs">
                    <Text c="gray" size="sm">
                      Control type
                    </Text>
                    <Text fw={500}>{tsd.customsState.control?.type ?? NOT_AVAILABLE}</Text>
                  </Stack>
                </Grid.Col>
                <Grid.Col span={3}>
                  <Stack gap="xs">
                    <Text c="gray" size="sm">
                      Control date and time
                    </Text>
                    <Text fw={500}>{tsd.customsState.control?.dateAndTime?.toString() ?? NOT_AVAILABLE}</Text>
                  </Stack>
                </Grid.Col>
              </Grid>
              <Grid>
                <Grid.Col span={3}>
                  <Stack gap="xs">
                    <Text c="gray" size="sm">
                      Control status code
                    </Text>
                    <Text fw={500}>{tsd.customsState.control?.statusReasonCode ?? NOT_AVAILABLE}</Text>
                  </Stack>
                </Grid.Col>
                <Grid.Col span={3}>
                  <Stack gap="xs">
                    <Text c="gray" size="sm">
                      Control status reason
                    </Text>
                    <Text fw={500}>{getControlStatusReason(tsd.customsState.control?.statusReasonCode) ?? NOT_AVAILABLE}</Text>
                  </Stack>
                </Grid.Col>{' '}
                <Grid.Col span={3}>
                  <Stack gap="xs">
                    <Text c="gray" size="sm">
                      Control remarks
                    </Text>
                    <Text fw={500}>{tsd.customsState.control?.remarks ?? NOT_AVAILABLE}</Text>
                  </Stack>
                </Grid.Col>
              </Grid>
            </Stack>
          )}
          {tsd.customsState?.customsDeclarationRejection && (
            <Stack mb="md">
              <Grid>
                <Grid.Col span={3}>
                  <Stack gap="xs">
                    <Text c="gray" size="sm">
                      Business rejection type
                    </Text>
                    <Text fw={500}>{tsd.customsState.customsDeclarationRejection.businessRejectionType ?? NOT_AVAILABLE}</Text>
                  </Stack>
                </Grid.Col>
                <Grid.Col span={3}>
                  <Stack gap="xs">
                    <Text c="gray" size="sm">
                      Rejection reason
                    </Text>
                    <Text fw={500}>{tsd.customsState.customsDeclarationRejection.rejectionReason ?? NOT_AVAILABLE}</Text>
                  </Stack>
                </Grid.Col>
                <Grid.Col span={3}>
                  <Stack gap="xs">
                    <Text c="gray" size="sm">
                      Rejection date
                    </Text>
                    <Text fw={500}>{tsd.customsState.customsDeclarationRejection.rejectionDate.toString()}</Text>
                  </Stack>
                </Grid.Col>
              </Grid>
            </Stack>
          )}
          {!!tsd.customsState?.functionalError?.length && (
            <Stack mt="xs">
              {tsd.customsState?.functionalError?.map((item, index) => (
                <Stack key={index} mb="lg">
                  <Title order={5} fw={600}>
                    {capitalize(item.errorType)} error
                  </Title>
                  <Grid mb="xs">
                    <Grid.Col span={2}>
                      {Number(item.sequenceNumber) >= 0 && (
                        <Stack>
                          <Text c="gray" size="sm">
                            Sequence number
                          </Text>
                          <Text fw={500}>{item.sequenceNumber}</Text>
                        </Stack>
                      )}
                    </Grid.Col>
                    <Grid.Col span={2}>
                      <Stack>
                        <Text c="gray" size="sm">
                          Error code
                        </Text>
                        <Text fw={500}>{item.errorCode}</Text>
                      </Stack>
                    </Grid.Col>
                    <Grid.Col span={3}>
                      <Stack>
                        <Text c="gray" size="sm">
                          Error code description
                        </Text>
                        <Text fw={500}>{getErrorDescription(item.errorType, item.errorCode)}</Text>
                      </Stack>
                    </Grid.Col>
                  </Grid>

                  <Stack mb="xs">
                    <Text c="gray" size="sm">
                      Error pointer
                    </Text>
                    <Text fw={500}>{item.errorPointer}</Text>
                  </Stack>

                  <Stack mb="xs">
                    <Text c="gray" size="sm">
                      Error reason
                    </Text>
                    <Text fw={500}>{item.errorReason}</Text>
                  </Stack>

                  {!!item.remarks?.length && (
                    <Stack mb="xs">
                      <Text c="gray" size="sm">
                        Remarks
                      </Text>
                      <Text fw={500}>{item.remarks}</Text>
                    </Stack>
                  )}

                  {!!item.originalAttributeValue?.length && (
                    <Stack mb="xs">
                      <Text c="gray" size="sm">
                        Original Attribute
                      </Text>
                      <Text fw={500}>{item.originalAttributeValue}</Text>
                    </Stack>
                  )}
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>

        <InvalidationSection tsd={tsd} />

        {tsd.customsState?.notifications?.length > 0 && <CustomsStateNotifications tsd={tsd} />}
      </Stack>
    </ScrollArea>
  );
};
